import { useQuery } from "@apollo/client";
import { faDownload, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import Axios, { AxiosResponse } from "axios";
import { BulmaButton, SubmitButton } from "Components/Button";
import { Horizontal } from "Components/Horizontal";
import { ButtonIcon } from "Components/Icon";
import { Modal } from "Components/Modal";
import * as DateFns from "date-fns";
import {
  CaseStatusFilter,
  ClaimStatusFilter,
  getDownloadAdminReportUrl,
  getDownloadAspenBordereauUrl,
  getDownloadPartnerReportUrl,
  getDownloadPolicyholderSummaryUrl,
  getDownloadUnpurchasedAppsReportUrl,
} from "downloadHelpers";
import { partnersQuery } from "generated/graphql";
import { DateInput } from "Inputs/DateInput";
import { Dropdown, enumToDropdownOptions } from "Inputs/Dropdown";
import {
  GetFormValues,
  GetSubmitType,
  useFormWithSchema,
} from "Inputs/FormTools";
import { ErrorMessage, YupDefined } from "Inputs/Tools";
import fileDownload from "js-file-download";
import * as querystring from "query-string";
import React, { useContext, useState } from "react";
import { formatISODate, hasValue, hv } from "Tools";
import * as Yup from "yup";
import { UserContext } from "../UserProvider";

type FormValues = GetFormValues<typeof useComponentForm>;
type SubmitValues = GetSubmitType<typeof useComponentForm>;

export function ReportingFieldset() {
  const result = useQuery(partnersQuery);
  const [error, setError] = useState<string | null>(null);
  const form = useComponentForm({ setError });

  return (
    <div className="box narrowFieldBodies3" style={{ textAlign: "center" }}>
      {error !== null && (
        <div style={{ marginBottom: "1em" }}>
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      )}
      <Horizontal {...form.field.reportType.getLabelFromSchema()}>
        <Dropdown
          field={form.field.reportType}
          options={enumToDropdownOptions(ReportType)}
        />
      </Horizontal>
      {(form.values.reportType === ReportType.AdminReport ||
        form.values.reportType === ReportType.PolicyholderSummary) && (
        <>
          <Horizontal {...form.field.caseStatusFilter.getLabelFromSchema()}>
            <Dropdown
              field={form.field.caseStatusFilter}
              options={[
                [CaseStatusFilter.Open, "Open"],
                [CaseStatusFilter.Closed, "Closed"],
                [CaseStatusFilter.All, "All"],
              ]}
            />
          </Horizontal>
          <Horizontal {...form.field.claimStatusFilter.getLabelFromSchema()}>
            <Dropdown
              field={form.field.claimStatusFilter}
              options={[
                [ClaimStatusFilter.UnderReview, "Under Review"],
                [ClaimStatusFilter.Paid, "Paid"],
                [ClaimStatusFilter.Denied, "Denied"],
                [ClaimStatusFilter.HasClaim, "Has Claim (Any Status)"],
                [ClaimStatusFilter.NoClaim, "No Claim"],
                [ClaimStatusFilter.All, "All"],
              ]}
            />
          </Horizontal>
        </>
      )}
      {form.values.reportType === ReportType.PartnerReport && (
        <Horizontal label="Partner Name" isRequired>
          {!hv(result.data) ? (
            <>Loading...</>
          ) : (
            <Dropdown
              field={form.field.partnerId}
              options={result.data.partners.map(p => [p.id.toString(), p.name])}
              placeholder="Select partner..."
            />
          )}
        </Horizontal>
      )}
      <Horizontal label="First date" isRequired>
        <DateInput field={form.field.getSubField("firstDate")} />
      </Horizontal>
      <Horizontal label="Last date" isRequired>
        <DateInput field={form.field.getSubField("lastDate")} />
      </Horizontal>
      {shouldEmailReport(form.values) ? (
        <EmailReportButton
          reportType={form.values.reportType}
          isSubmitting={form.isSubmitting}
          submitForm={form.submitForm}
        />
      ) : (
        <SubmitButton
          isSubmitting={form.isSubmitting}
          onClick={() => form.submitForm()}
        >
          <ButtonIcon icon={faDownload} />
          <span>Download Report</span>
        </SubmitButton>
      )}
    </div>
  );
}

function useComponentForm(options: {
  setError: (error: string | null) => void;
}) {
  const today = DateFns.startOfDay(new Date());
  return useFormWithSchema({
    initialValues: {
      reportType: ReportType.AdminReport,
      caseStatusFilter: CaseStatusFilter.All,
      claimStatusFilter: ClaimStatusFilter.All,
      firstDate: DateFns.startOfMonth(today),
      lastDate: today,
      partnerId: null,
    },
    onSubmit: async values => {
      options.setError(null);
      if (shouldEmailReport(values)) {
        const sendReportEmailUrl = getSendReportEmailUrl(values);
        await Axios.get(sendReportEmailUrl, { responseType: "blob" });
        return;
      }
      const downloadUrl = getDownloadReportUrl(values);
      const result: AxiosResponse<Blob> = await Axios.get(downloadUrl, {
        responseType: "blob",
      });
      if (result.status === 204) {
        options.setError("There is no report data for the selected period.");
        return;
      }
      const contentDisposition: string | undefined =
        result.headers["content-disposition"];
      const filenameMatch = contentDisposition?.match(/filename\s*=\s*"(.+)"/i);
      const filename =
        !hasValue(filenameMatch) || filenameMatch.length <= 1
          ? null
          : filenameMatch[1];
      fileDownload(
        result.data,
        filename ?? "download.xlsx",
        result.headers["content-type"] as string | undefined,
      );
    },
    validationSchema: Yup.object({
      reportType: YupDefined.enum(ReportType).label("Report Type").required(),
      caseStatusFilter: YupDefined.enum(CaseStatusFilter)
        .label("Case Status")
        .required(),
      claimStatusFilter: YupDefined.enum(ClaimStatusFilter)
        .label("Claim Status")
        .required(),
      firstDate: DateInput.getDefaultValidator().label("First Date"),
      lastDate: DateInput.getDefaultValidator()
        .label("Last Date")
        .min(
          Yup.ref("firstDate"),
          "Please select a Last date that is on or after the First date.",
        ),
      partnerId: YupDefined.string()
        .nullable()
        .label("Partner Name")
        .when("reportType", {
          is: ReportType.PartnerReport,
          then: schema => schema.required(),
        }),
    }),
  });
}

enum ReportType {
  AdminReport = "Admin Report",
  AspenBordereau = "Aspen Bordereau",
  PolicyholderSummary = "Policyholder Summary",
  UnpurchasedApplications = "Unpurchased Applications",
  PartnerReport = "Partner Report",
}

function getDownloadReportUrl(values: SubmitValues) {
  switch (values.reportType) {
    case ReportType.AdminReport:
      return getDownloadAdminReportUrl(
        values.firstDate,
        values.lastDate,
        values.caseStatusFilter,
        values.claimStatusFilter,
      );
    case ReportType.AspenBordereau:
      return getDownloadAspenBordereauUrl(values.firstDate, values.lastDate);
    case ReportType.PolicyholderSummary:
      return getDownloadPolicyholderSummaryUrl(
        values.firstDate,
        values.lastDate,
        values.caseStatusFilter,
        values.claimStatusFilter,
      );
    case ReportType.UnpurchasedApplications:
      return getDownloadUnpurchasedAppsReportUrl(
        values.firstDate,
        values.lastDate,
      );
    case ReportType.PartnerReport:
      return getDownloadPartnerReportUrl(
        Number(values.partnerId!),
        values.firstDate,
        values.lastDate,
      );
  }
}

function getSendReportEmailUrl(values: SubmitValues) {
  return `/SendReportEmail?${querystring.stringify({
    reportType: values.reportType,
    startDate: formatISODate(values.firstDate),
    lastDate: formatISODate(values.lastDate),
    partnerId: values.partnerId,
    caseStatusFilter: values.caseStatusFilter,
    claimStatusFilter: values.claimStatusFilter,
  })}`;
}

function shouldEmailReport(values: FormValues) {
  return values.firstDate && values.lastDate
    ? DateFns.differenceInMonths(values.lastDate, values.firstDate) >= 1
    : false;
}

function EmailReportButton(props: {
  reportType: ReportType;
  isSubmitting: boolean;
  submitForm: () => Promise<any>;
}) {
  const { user } = useContext(UserContext);
  const [isModalVisible, setModalVisible] = useState(false);
  return (
    <>
      <Modal
        title=""
        isVisible={isModalVisible}
        onCancel={() => setModalVisible(false)}
        buttonRow={
          <BulmaButton
            className="is-primary"
            isSubmitting={props.isSubmitting}
            onClick={() => setModalVisible(false)}
          >
            OK
          </BulmaButton>
        }
      >
        The requested {props.reportType} file is being generated and will be
        emailed to {user!.email} when it is ready.
      </Modal>
      <SubmitButton
        isSubmitting={props.isSubmitting}
        onClick={async () => {
          await props.submitForm();
          setModalVisible(true);
        }}
      >
        <ButtonIcon icon={faEnvelope} />
        <span>Email Report</span>
      </SubmitButton>
    </>
  );
}
